/**
 * RoFileUpload - 图片/视频上传 Web 组件
 *
 * 功能特性：
 * - Shadow DOM；选文件 → 客户端校验（扩展名、图片/视频分档大小、数量上限）→ 上传中占位 → 缩略预览
 * - 默认限制与扩展名来自活动表单常用值（仅作未配置属性时的默认值，无业务语义）
 * - 使用 fetch + FormData 发起 POST；上传地址见 _resolveFetchUrl（有 uploadHandler 时忽略）：
 *   · upload-url 非空：整段 URL（相对则相对当前页 origin）
 *   · 否则 upload-base-url + upload-path：如 https://s-api-eu.roborock.com 与 /api/file/upload
 *   · 否则仅 upload-path 相对当前页 origin；path 默认 /api/file/upload
 * - 文件字段名：form-field-name（默认 file）；
 * - upload-source：单独映射后端 source；
 * - form-data-extra：JSON 追加其它表单项（勿含 source；若含则以 upload-source 为准）
 * - 请求头：x-country 映射 X-Country；与 headers-extra 对象合并，同键以 headers-extra 为准；过滤会破坏 multipart 的 Content-Type
 * - 声明式解析：response-ok-key + response-ok-value 判定成功；response-url-key 支持点路径；未设属性时默认 result / 1 / data（与常见活动上传 JSON 一致）。显式设 response-ok-key=""（空串）可跳过成功字段校验
 * - 实例属性（优先于 window）：uploadHandler(file, { signal }) => Promise<string>；responseParser(json) => string
 * - 属性 parse-response：window 上的函数名，不推荐，仅作兜底
 * - 可选 heic-convert + 页面 heic2any：HEIC 转 JPEG 再上传
 * - Form-Associated：setFormValue 为 JSON 数组字符串；required / validate()；formResetCallback 调用 reset()
 * - 事件：change（detail.urls）、error（detail.message）；上传过程派发 upload-start / upload-end
 * - 错误文案：layout 注入 window.RoFormLocale（general.form_validation.*）；兜底 DEFAULT_MESSAGES；必填可设 error-message-required（ro-form-locale.liquid 中 RoInputLocale 为历史别名）
 *
 * 声明式三属性（无 responseParser / parse-response 时；均有默认值 result / 1 / data）：
 * - response-ok-key：成功判断字段；未设属性用默认；显式空字符串可跳过该步
 * - response-ok-value：与 JSON 宽松相等；未设属性用默认 1
 * - response-url-key：点分路径取 URL；未设属性用默认 data
 *
 * 使用方法：
 * 1. 域名 + 路径拆分：
 *    <ro-file-upload
 *      upload-base-url="https://s-api-eu.roborock.com"
 *      upload-path="/api/file/upload"
 *    ></ro-file-upload>
 *
 * 2. 单字段 upload-url：
 *    <ro-file-upload upload-url="/api/file/upload"></ro-file-upload>
 *
 * 3. 声明式解析（扁平 data）：
 *    <ro-file-upload
 *      upload-url="/api/file/upload"
 *      upload-source="christmas"
 *      x-country="{{ language }}"
 *      response-ok-key="result"
 *      response-ok-value="1"
 *      response-url-key="data"
 *    ></ro-file-upload>
 *
 * 4. 嵌套 URL（payload.url）：
 *    <ro-file-upload
 *      response-ok-key="result"
 *      response-ok-value="1"
 *      response-url-key="payload.url"
 *    ></ro-file-upload>
 *
 * 5. headers-extra 覆盖 X-Country：
 *    <ro-file-upload x-country="us" headers-extra='{"X-Country":"de"}'></ro-file-upload>
 *
 * 6. 模块内实例解析（推荐复杂逻辑）：
 *    <ro-file-upload id="f"></ro-file-upload>
 *    <script type="module">
 *      const el = document.getElementById("f");
 *      el.responseParser = (json) => { ...; return url; };
 *    </script>
 *
 * 7. 完全自定义上传：
 *    el.uploadHandler = async (file, { signal }) => { ...; return url; };
 *
 * 8. 监听与表单：
 *    el.addEventListener("change", (e) => console.log(e.detail.urls));
 *    el.getUrls(); el.reset(); el.validate();
 */
class RoFileUpload extends HTMLElement {
  static formAssociated = true;

  /** 未配置 upload-url、且未显式配置 upload-path 时与页面 origin 拼接的默认路径 */
  static DEFAULT_UPLOAD_PATH = "/api/file/upload";

  /** 声明式解析默认：与 activity 上传接口常见 { result: 1, data: url } 对齐 */
  static DEFAULT_RESPONSE_OK_KEY = "result";
  static DEFAULT_RESPONSE_OK_VALUE = "1";
  static DEFAULT_RESPONSE_URL_KEY = "data";

  /** 与 activity-ugc-from.liquid 685–694 行一致，作未设属性时的默认值 */
  static DEFAULT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  static DEFAULT_MAX_VIDEO_BYTES = 65 * 1024 * 1024;
  static DEFAULT_MAX_FILES = 5;
  static DEFAULT_IMAGE_EXTS = [
    ".jpeg", ".jpg", ".png", ".gif", ".svg", ".bmp", ".tiff", ".tif",
    ".webp", ".heic", ".ico", ".avif",
  ];
  static DEFAULT_VIDEO_EXTS = [
    ".mp4", ".avi", ".mkv", ".mov", ".wmv", ".flv", ".webm", ".3gp", ".m4v", ".asf",
  ];

  /** 无 RoFormLocale 时的英文兜底（与 en.default general.form_validation 对齐） */
  static DEFAULT_MESSAGES = {
    file_upload_max_files: "Maximum number of files reached",
    file_upload_invalid_type: "Unsupported file type",
    file_upload_image_too_large: "Image file is too large",
    file_upload_video_too_large: "Video file is too large",
    file_upload_heic_failed: "HEIC conversion failed",
    file_upload_required: "Please upload at least one file",
    file_upload_no_url: "No URL in response",
    file_upload_invalid_json: "Invalid JSON response",
    file_upload_rejected: "Upload rejected by server",
    file_upload_empty_result: "Empty upload result",
    file_upload_cancelled: "Upload cancelled",
    file_upload_failed: "Upload failed",
    file_upload_parse_response_invalid: '"{{ name }}" is not a function on window (parse-response)',
  };

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._internals = this.attachInternals();

    /** @type {string[]} */
    this._urls = [];
    this._uploading = false;
    this._warnedExtraSource = false;

    /** @type {(file: File, init: { signal: AbortSignal }) => Promise<string> | null} */
    this.uploadHandler = null;
    /** @type {((json: unknown) => string) | null} */
    this.responseParser = null;

    this._abortController = null;

    this._onFileChange = this._onFileChange.bind(this);
    this._onAddClick = this._onAddClick.bind(this);

    this._renderStatic();
  }

  _renderStatic() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          --rofu-cell: 56px;
          --rofu-add-pad: 24px;
          --rofu-border: var(--border-color, #e1e1e1);
          --rofu-plus-bg: var(--bg-white-color, #fff);
        }
        :host([disabled]) { opacity: 0.6; pointer-events: none; }
        .wrap { display: flex; flex-direction: column; gap: 8px; }
        .grid {
          display: flex;
          flex-wrap: wrap;
          gap: calc(var(--body-font-size, 100) * 0.08px);
          gap: 8px;
        }
        .cell {
          width: var(--rofu-cell);
          height: var(--rofu-cell);
          border-radius: 6px;
          overflow: hidden;
          flex-shrink: 0;
          position: relative;
          box-sizing: border-box;
        }
        .thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          background: #f5f5f5;
        }
        .thumb-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          background: #000;
        }
        .add-cell {
          border: 1px solid var(--rofu-border);
          background: var(--rofu-plus-bg);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: var(--rofu-add-pad);
          box-sizing: border-box;
        }
        :host([disabled]) .add-cell { cursor: not-allowed; }
        .add-inner {
          width: 40px;
          height: 40px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .add-inner svg { width: 15px; height: 15px; display: block; color: #666; }
        .loading-cell {
          border: 1px solid var(--rofu-border);
          background: #e3e0d6;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .loader {
          width: 28%;
          height: 28%;
          min-width: 20px;
          min-height: 20px;
          border: 1px solid #909090;
          border-top-color: #d6d6d6;
          border-radius: 50%;
          animation: rofu-spin 2s linear infinite;
        }
        @keyframes rofu-spin {
          to { transform: rotate(360deg); }
        }
        .file-input {
          position: absolute;
          width: 0;
          height: 0;
          opacity: 0;
          pointer-events: none;
        }
        .error-message {
          display: none;
          color: var(--error-color, #d93840);
          font-size: var(--error-font-size, 12px);
          line-height: var(--error-line-height, 16px);
        }
        :host([error]) .error-message { display: block; }
      </style>
      <div class="wrap">
        <div class="grid" part="grid"></div>
        <input type="file" class="file-input" />
        <div class="error-message" part="error"></div>
      </div>
    `;
    this._gridEl = this.shadowRoot.querySelector(".grid");
    this._fileInput = this.shadowRoot.querySelector(".file-input");
    this._errorEl = this.shadowRoot.querySelector(".error-message");
  }

  connectedCallback() {
    this._fileInput.addEventListener("change", this._onFileChange);
    this._syncFromAttributes();
    this._refreshGrid();
    this._syncFormValue();
  }

  disconnectedCallback() {
    this._fileInput.removeEventListener("change", this._onFileChange);
    if (this._abortController) this._abortController.abort();
  }

  static get observedAttributes() {
    return [
      "upload-url",
      "upload-base-url",
      "upload-path",
      "upload-source",
      "form-data-extra",
      "form-field-name",
      "x-country",
      "headers-extra",
      "response-ok-key",
      "response-ok-value",
      "response-url-key",
      "parse-response",
      "error-message-required",
      "max-files",
      "max-image-size",
      "max-video-size",
      "accept-image",
      "accept-video",
      "heic-convert",
      "disabled",
      "required",
      "name",
      "method",
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this._syncFromAttributes();
    if (name === "disabled" || name.startsWith("max-") || name.startsWith("accept-")) this._refreshGrid();
  }

  _syncFromAttributes() {
    const disabled = this.hasAttribute("disabled");
    this._fileInput.disabled = disabled;
    const acc = this._buildAccept();
    this._fileInput.setAttribute("accept", acc);
  }

  _buildAccept() {
    const img = this._parseExtList(this.getAttribute("accept-image"), RoFileUpload.DEFAULT_IMAGE_EXTS);
    const vid = this._parseExtList(this.getAttribute("accept-video"), RoFileUpload.DEFAULT_VIDEO_EXTS);
    const all = [...new Set([...img, ...vid])];
    return all.join(",");
  }

  /**
   * 将逗号分隔扩展名规范为带点、小写；空属性则返回 defaults
   */
  _parseExtList(attr, defaults) {
    if (attr == null || String(attr).trim() === "") return [...defaults];
    return String(attr)
      .split(/[,，\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
      .map((s) => (s.startsWith(".") ? s : `.${s}`));
  }

  _getMaxFiles() {
    const a = this.getAttribute("max-files");
    if (a == null || String(a).trim() === "") return RoFileUpload.DEFAULT_MAX_FILES;
    const n = parseInt(a, 10);
    return Number.isFinite(n) && n > 0 ? n : RoFileUpload.DEFAULT_MAX_FILES;
  }

  _getMaxImage() {
    const a = this.getAttribute("max-image-size");
    if (a == null || String(a).trim() === "") return RoFileUpload.DEFAULT_MAX_IMAGE_BYTES;
    const n = parseInt(a, 10);
    return Number.isFinite(n) && n > 0 ? n : RoFileUpload.DEFAULT_MAX_IMAGE_BYTES;
  }

  _getMaxVideo() {
    const a = this.getAttribute("max-video-size");
    if (a == null || String(a).trim() === "") return RoFileUpload.DEFAULT_MAX_VIDEO_BYTES;
    const n = parseInt(a, 10);
    return Number.isFinite(n) && n > 0 ? n : RoFileUpload.DEFAULT_MAX_VIDEO_BYTES;
  }

  _getImgExts() {
    return this._parseExtList(this.getAttribute("accept-image"), RoFileUpload.DEFAULT_IMAGE_EXTS);
  }

  _getVidExts() {
    return this._parseExtList(this.getAttribute("accept-video"), RoFileUpload.DEFAULT_VIDEO_EXTS);
  }

  _isImageName(name) {
    const n = name.toLowerCase();
    return this._getImgExts().some((ext) => n.endsWith(ext));
  }

  _isVideoName(name) {
    const n = name.toLowerCase();
    return this._getVidExts().some((ext) => n.endsWith(ext));
  }

  _applyParams(msg, params = {}) {
    if (msg == null || msg === "") return msg;
    let out = String(msg);
    if (params && Object.keys(params).length) {
      Object.entries(params).forEach(([k, v]) => {
        out = out.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g"), String(v));
      });
    }
    return out;
  }

  /**
   * 顺序：RoFormLocale → DEFAULT_MESSAGES；支持 {{ key }} 占位符
   * @param {string} key
   * @param {Record<string, string | number>} [params]
   */
  _getUploadMessage(key, params = {}) {
    let msg = window.RoFormLocale?.[key];
    if (msg != null && msg !== "") return this._applyParams(msg, params);
    msg = RoFileUpload.DEFAULT_MESSAGES[key];
    if (msg != null && msg !== "") return this._applyParams(msg, params);
    return "";
  }

  _openPicker() {
    this._fileInput.click();
  }

  _onAddClick(e) {
    e.preventDefault();
    if (this._uploading || this.hasAttribute("disabled")) return;
    if (this._urls.length >= this._getMaxFiles()) return;
    this._openPicker();
  }

  async _onFileChange() {
    const input = this._fileInput;
    const file = input.files && input.files[0];
    input.value = "";
    if (!file || this.hasAttribute("disabled")) return;
    if (this._urls.length >= this._getMaxFiles()) {
      this._showErrorUi(this._getUploadMessage("file_upload_max_files"), false);
      return;
    }

    const ok = this._validateFile(file);
    if (!ok) return;

    await this._uploadFile(file);
  }

  _validateFile(file) {
    const name = file.name || "";
    const isImg = this._isImageName(name);
    const isVid = this._isVideoName(name);
    if (!isImg && !isVid) {
      this._showErrorUi(this._getUploadMessage("file_upload_invalid_type"), false);
      return false;
    }
    const max = isVid ? this._getMaxVideo() : this._getMaxImage();
    if (file.size > max) {
      this._showErrorUi(
        this._getUploadMessage(isVid ? "file_upload_video_too_large" : "file_upload_image_too_large"),
        false,
      );
      return false;
    }
    this._clearErrorUi(false);
    return true;
  }

  async _prepareFileBody(file) {
    const name = (file.name || "").toLowerCase();
    const useHeic =
      this.hasAttribute("heic-convert") &&
      name.endsWith(".heic") &&
      typeof window.heic2any === "function";
    if (!useHeic) return file;
    try {
      const blob = await window.heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 1,
      });
      const b = Array.isArray(blob) ? blob[0] : blob;
      return new File([b], name.replace(/\.heic$/i, ".jpg"), { type: "image/jpeg" });
    } catch {
      this._showErrorUi(this._getUploadMessage("file_upload_heic_failed"), false);
      return null;
    }
  }

  /**
   * 按 response-ok-key / response-ok-value 校验成功，再用 response-url-key 点路径取值。
   * - response-url-key 未设时用 DEFAULT_RESPONSE_URL_KEY（data）
   * - response-ok-key 未设（null）时用 DEFAULT_RESPONSE_OK_KEY（result）；显式空字符串则跳过 ok 校验
   * - response-ok-value 未设或为空时用 DEFAULT_RESPONSE_OK_VALUE（1）
   */
  _parseDeclarative(json) {
    const okKeyAttr = this.getAttribute("response-ok-key");
    let okKey;
    if (okKeyAttr === null) okKey = RoFileUpload.DEFAULT_RESPONSE_OK_KEY;
    else if (String(okKeyAttr).trim() === "") okKey = null;
    else okKey = String(okKeyAttr).trim();

    const okValRaw = this.getAttribute("response-ok-value");
    const okValNorm =
      okValRaw != null && String(okValRaw).trim() !== ""
        ? String(okValRaw).trim()
        : RoFileUpload.DEFAULT_RESPONSE_OK_VALUE;

    if (okKey != null) {
      const raw = RoFileUpload._getByPath(json, okKey);
      if (!RoFileUpload._looseEqual(raw, okValNorm)) {
        throw new Error(
          typeof json === "object" && json && "msg" in json && json.msg
            ? String(json.msg)
            : this._getUploadMessage("file_upload_rejected"),
        );
      }
    }

    const urlKeyRaw = this.getAttribute("response-url-key");
    const urlKey =
      urlKeyRaw != null && String(urlKeyRaw).trim() !== ""
        ? String(urlKeyRaw).trim()
        : RoFileUpload.DEFAULT_RESPONSE_URL_KEY;
    const url = RoFileUpload._getByPath(json, urlKey);
    if (url == null || url === "") throw new Error(this._getUploadMessage("file_upload_no_url"));
    return String(url);
  }

  /**
   * 沿点分路径下钻；段为对象键，遇 null/undefined 则返回 undefined。
   */
  static _getByPath(obj, path) {
    if (obj == null || path == null) return undefined;
    const parts = String(path).split(".").filter(Boolean);
    let cur = obj;
    for (const p of parts) {
      if (cur == null || typeof cur !== "object") return undefined;
      cur = cur[p];
    }
    return cur;
  }

  /** 属性字符串与 JSON 值的宽松相等（数字 1 与 "1"） */
  static _looseEqual(jsonVal, attrVal) {
    if (attrVal == null || String(attrVal).trim() === "") return jsonVal == null || jsonVal === "";
    if (jsonVal == null) return false;
    if (typeof jsonVal === "number" && !Number.isNaN(jsonVal)) {
      const n = Number(attrVal);
      if (!Number.isNaN(n)) return jsonVal === n;
    }
    return String(jsonVal) === String(attrVal);
  }

  /**
   * 解析内置 fetch 的最终 URL。
   * 优先级：upload-url（非空）> upload-base-url + upload-path > 仅 upload-path（默认 /api/file/upload）相对 location.origin
   */
  _resolveFetchUrl() {
    const single = this.getAttribute("upload-url");
    if (single != null && String(single).trim() !== "") {
      return new URL(String(single).trim(), window.location.origin).href;
    }
    const pathAttr = this.getAttribute("upload-path");
    const path =
      pathAttr != null && String(pathAttr).trim() !== ""
        ? String(pathAttr).trim()
        : RoFileUpload.DEFAULT_UPLOAD_PATH;
    const normPath = path.startsWith("/") ? path : "/" + path;
    const base = this.getAttribute("upload-base-url");
    if (base != null && String(base).trim() !== "") {
      let b = String(base).trim();
      while (b.endsWith("/")) b = b.slice(0, -1);
      return new URL(normPath, b + "/").href;
    }
    return new URL(normPath, window.location.origin).href;
  }

  _mergeHeaders() {
    const base = {};
    const xc = this.getAttribute("x-country");
    if (xc != null && String(xc).trim() !== "") {
      base["X-Country"] = String(xc).trim();
    }
    const extraRaw = this.getAttribute("headers-extra");
    let extra = {};
    if (extraRaw != null && String(extraRaw).trim() !== "") {
      try {
        extra = JSON.parse(extraRaw);
        if (extra == null || typeof extra !== "object" || Array.isArray(extra)) extra = {};
      } catch {
        extra = {};
      }
    }
    const merged = { ...base, ...extra };
    const out = {};
    for (const [k, v] of Object.entries(merged)) {
      if (v == null) continue;
      const key = String(k);
      const val = String(v);
      const low = key.toLowerCase();
      if (low === "content-type") {
        const lv = val.toLowerCase();
        if (lv.includes("multipart/form-data") && !lv.includes("boundary=")) continue;
      }
      out[key] = val;
    }
    return out;
  }

  _buildFormData(file) {
    const fd = new FormData();
    const field = this.getAttribute("form-field-name") || "file";
    fd.append(field, file);

    const src = this.getAttribute("upload-source");
    if (src != null && String(src).trim() !== "") {
      fd.append("source", String(src).trim());
    }

    const extraRaw = this.getAttribute("form-data-extra");
    if (extraRaw != null && String(extraRaw).trim() !== "") {
      try {
        const ex = JSON.parse(extraRaw);
        if (ex && typeof ex === "object" && !Array.isArray(ex)) {
          for (const [k, v] of Object.entries(ex)) {
            if (v == null) continue;
            if (k === "source") {
              if (!this._warnedExtraSource) {
                console.warn("[ro-file-upload] form-data-extra contains \"source\"; upload-source will take precedence.");
                this._warnedExtraSource = true;
              }
              continue;
            }
            fd.append(k, typeof v === "string" ? v : JSON.stringify(v));
          }
        }
      } catch {
        /* ignore bad JSON */
      }
    }
    return fd;
  }

  async _resolveUrlFromResponseJson(json) {
    if (typeof this.responseParser === "function") {
      return this.responseParser(json);
    }
    const fnName = this.getAttribute("parse-response");
    if (fnName != null && String(fnName).trim() !== "") {
      const fn = window[String(fnName).trim()];
      if (typeof fn !== "function") {
        throw new Error(
          this._getUploadMessage("file_upload_parse_response_invalid", { name: String(fnName).trim() }),
        );
      }
      return fn(json);
    }
    return this._parseDeclarative(json);
  }

  async _uploadFile(file) {
    const body = await this._prepareFileBody(file);
    if (body == null) return;

    this._uploading = true;
    this._abortController = new AbortController();
    this._refreshGrid();
    this.dispatchEvent(
      new CustomEvent("upload-start", { bubbles: true, composed: true, detail: { name: file.name } }),
    );

    try {
      let url;
      if (typeof this.uploadHandler === "function") {
        url = await this.uploadHandler(body, { signal: this._abortController.signal });
      } else {
        const resolved = this._resolveFetchUrl();
        const method = (this.getAttribute("method") || "POST").toUpperCase();
        const headers = this._mergeHeaders();
        const res = await fetch(resolved, {
          method,
          headers,
          body: this._buildFormData(body),
          signal: this._abortController.signal,
        });
        const text = await res.text();
        let json;
        try {
          json = text ? JSON.parse(text) : {};
        } catch {
          throw new Error(this._getUploadMessage("file_upload_invalid_json"));
        }
        if (!res.ok) {
          const msg =
            typeof json === "object" && json && "msg" in json
              ? String(json.msg)
              : `HTTP ${res.status}`;
          throw new Error(msg);
        }
        url = await this._resolveUrlFromResponseJson(json);
      }
      if (url == null || String(url).trim() === "") throw new Error(this._getUploadMessage("file_upload_empty_result"));
      this._urls.push(String(url).trim());
      this._clearErrorUi(true);
      this._syncFormValue();
      this.dispatchEvent(
        new CustomEvent("change", { bubbles: true, composed: true, detail: { urls: [...this._urls] } }),
      );
    } catch (e) {
      const msg =
        e && e.name === "AbortError"
          ? this._getUploadMessage("file_upload_cancelled")
          : (e && e.message) || this._getUploadMessage("file_upload_failed");
      this._showErrorUi(msg, false);
      this.dispatchEvent(new CustomEvent("error", { bubbles: true, composed: true, detail: { message: msg } }));
    } finally {
      this._uploading = false;
      this._abortController = null;
      this._refreshGrid();
      this.dispatchEvent(new CustomEvent("upload-end", { bubbles: true, composed: true }));
    }
  }

  _syncFormValue() {
    const payload = JSON.stringify(this._urls);
    this._internals.setFormValue(payload);
  }

  /**
   * @param {string} msg
   * @param {boolean} affectForm - true 时同步 ElementInternals，供 validate() / 必填使用；上传失败仅为提示，不传 true
   */
  _showErrorUi(msg, affectForm) {
    this._errorEl.textContent = msg;
    this.setAttribute("error", "");
    if (affectForm) this._internals.setValidity({ customError: true }, msg);
  }

  /**
   * @param {boolean} affectForm
   */
  _clearErrorUi(affectForm) {
    this._errorEl.textContent = "";
    this.removeAttribute("error");
    if (affectForm) this._internals.setValidity({});
  }

  _refreshGrid() {
    const grid = this._gridEl;
    grid.innerHTML = "";
    const max = this._getMaxFiles();

    for (let i = 0; i < this._urls.length; i++) {
      const u = this._urls[i];
      const cell = document.createElement("div");
      cell.className = "cell";
      cell.setAttribute("part", `thumb-${i}`);
      if (this._isVideoUrl(u)) {
        const v = document.createElement("video");
        v.className = "thumb-video";
        v.setAttribute("playsinline", "");
        v.setAttribute("muted", "");
        v.src = u;
        cell.appendChild(v);
      } else {
        const img = document.createElement("img");
        img.className = "thumb";
        img.alt = "";
        img.src = u;
        cell.appendChild(img);
      }
      grid.appendChild(cell);
    }

    if (this._uploading) {
      const loadCell = document.createElement("div");
      loadCell.className = "cell loading-cell";
      loadCell.setAttribute("part", "loading");
      const loader = document.createElement("div");
      loader.className = "loader";
      loadCell.appendChild(loader);
      grid.appendChild(loadCell);
    } else if (this._urls.length < max && !this.hasAttribute("disabled")) {
      const add = document.createElement("button");
      add.type = "button";
      add.className = "cell add-cell";
      add.setAttribute("part", "add");
      add.setAttribute("aria-label", "Upload file");
      add.innerHTML = `
        <span class="add-inner">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
            <path d="M8.14258 6.14258H14.2861V8.14258H8.14258V14.2861H6.14258V8.14258H0V6.14258H6.14258V0H8.14258V6.14258Z" fill="#666666"/>
          </svg>
        </span>
      `;
      add.addEventListener("click", this._onAddClick);
      grid.appendChild(add);
    }
  }

  _isVideoUrl(url) {
    const lower = url.toLowerCase().split("?")[0];
    return this._getVidExts().some((ext) => lower.endsWith(ext));
  }

  // ---------- 公开 API ----------

  getUrls() {
    return [...this._urls];
  }

  reset() {
    this._urls = [];
    this._uploading = false;
    if (this._abortController) this._abortController.abort();
    this._abortController = null;
    this._clearErrorUi(true);
    this._fileInput.value = "";
    this._syncFormValue();
    this._refreshGrid();
    this.dispatchEvent(
      new CustomEvent("change", { bubbles: true, composed: true, detail: { urls: [] } }),
    );
  }

  validate() {
    const req = this.hasAttribute("required");
    if (!req) {
      this._clearErrorUi(true);
      this._internals.setValidity({});
      return true;
    }
    if (this._urls.length === 0) {
      const attr = this.getAttribute("error-message-required");
      const msg =
        attr != null && String(attr).trim() !== ""
          ? String(attr).trim()
          : this._getUploadMessage("file_upload_required");
      this._showErrorUi(msg, true);
      return false;
    }
    this._clearErrorUi(true);
    this._internals.setValidity({});
    return true;
  }

  formResetCallback() {
    this.reset();
  }

  formDisabledCallback(disabled) {
    this.toggleAttribute("disabled", disabled);
  }
}

customElements?.define("ro-file-upload", RoFileUpload);
